import { createApi } from "@reduxjs/toolkit/query/react";
import authBaseQuery from "../../../../utils/authBaseQuery";
import { use } from "react";

const OperationApi = createApi({
  reducerPath: "operationApi",
  baseQuery: authBaseQuery,
  tagTypes: ["Operation"],
  endpoints: (builder) => ({
    // Endpoint to fetch all PlanX data
    getAllPlanXData: builder.query({
      query: () => "v1/planxlogs",
      transformResponse: (response) => response?.data,
    }),

    FetchAllStats: builder.query({
      query: () => ({
        url: `v1/purchase/get_posts_stats_unfetched_posts`,
        transformResponse: (response) => response?.data,
      }),
    }),

    GetAllPagessByPlatform: builder.query({
      query: (platform) =>
        `v1/get_all_pages${platform ? `?platform_name=${platform}` : ""}`,
      transformResponse: (response) => response.data,
    }),

    getPlanById: builder.query({
      query: ({
        id,
        vendorId,
        startDate,
        endDate,
        page,
        limit,
        status,
        isInvoicePending,
        isCollab,
        isZeroAmt,
        search,
      }) => {
        let url = `v1/operation/get_post_details_by_campaign`;
        if (id && id !== 0 && id !== "null") {
          url += `/${id}`;
        }
        if (
          vendorId ||
          startDate ||
          endDate ||
          page ||
          limit ||
          status ||
          isInvoicePending ||
          isCollab ||
          isZeroAmt ||
          search
        ) {
          const params = new URLSearchParams();
          if (vendorId) params.append("vendorId", vendorId);
          if (startDate) params.append("startDate", startDate);
          if (endDate) params.append("endDate", endDate);
          if (page) params.append("page", page);
          if (limit) params.append("limit", limit);
          if (status) params.append("status", status);
          if (isCollab) params.append("isCollab", isCollab);
          if (isInvoicePending) params.append("isInvoicePending", isInvoicePending);
          if (isZeroAmt) params.append("isZeroAmt", isZeroAmt);
          if (search) params.append("search", search);

          url += `?${params.toString()}`;
        }
        return url;
      },
      transformResponse: (response) => response,
      providesTags: (result, error, arg) => [
        { type: "Plan", id: arg.id || "LIST" },
      ],
    }),

    getDeleteStoryData: builder.query({
      query: (id) => `v1/operation/exclude_story_from_post_link/${id}`,
    }),

    getPlanWiseData: builder.query({
      query: ({ selectPlan }) => {
        return `operation/operation_execution_master/${selectPlan}`;
      },
      transformResponse: (response) => response?.data,
    }),
    PlanDataUpload: builder.mutation({
      query: (data) => ({
        url: `v1/operation/campaign_post_details`,
        method: "POST",
        body: data,
      }),
    }),
    PlanDataUploadPlatformWise: builder.mutation({
      query: (data) => ({
        url: `v1/operation/add_campaign_post_for_platform`,
        method: "POST",
        body: data,
      }),
    }),
    getPostDetailofPagenVendor: builder.mutation({
      query: (data) => ({
        url: `v1/get_page_name_or_vendor_name_based_on_condition`,
        method: "POST",
        body: data,
      }),
    }),

    PostDataUpdate: builder.mutation({
      query: (data) => ({
        url: `v1/operation/update_campaign_post_data/${data.get("_id")}`,
        method: "PUT",
        body: data,
      }),
    }),

    BulkCampaignUpdate: builder.mutation({
      query: (data) => ({
        url: `v1/purchase/bulk_campaign_update`,
        method: "PUT",
        body: data,
      }),
    }),

    updatePhaseDate: builder.mutation({
      query: (data) => ({
        url: `v1/operation/update_phase_date`,
        method: "PUT",
        body: data,
      }),
    }),
    updateVendor: builder.mutation({
      query: (data) => ({
        url: `v1/operation/update_vendor_for_links`,
        method: "PUT",
        body: data,
      }),
    }),

    auditedDataUpload: builder.mutation({
      query: (data) => ({
        url: `v1/operation/record_purchase`,
        method: "POST",
        body: data,
      }),
    }),
    getAuditLogHistory: builder.query({
      query: (data) => ({
        url: `v1/audit-logs?entity_id=${data?._id}`,
        method: "GET",
      }),
    }),
    addStoryData: builder.mutation({
      query: (data) => ({
        url: `v1/operation/add_instagram_story`,
        method: "POST",
        body: data,
      }),
    }),

    vendorData: builder.query({
      query: (name) =>
        `v1/getPageMasterDetailByPageName/${name}?platform_name=instagram`,
      transformResponse: (response) => response?.data,
    }),
    updateMultipleAuditStatus: builder.mutation({
      query: (data) => ({
        url: `v1/operation/update_audit_status`,
        method: "PUT",
        body: data,
      }),
    }),
    updatePriceforPost: builder.mutation({
      query: (data) => ({
        url: `v1/operation/update_post_price/${data.shortCode}`,
        method: "PUT",
        body: data,
      }),
    }),
    handleFetchPrice: builder.mutation({
      query: (data) => ({
        url: `v1/sales/vendor_sales/update_post_price/${data.shortCode}`,
        method: "PUT",
        body: data,
      }),
    }),
    addMultipleService: builder.mutation({
      query: (data) => ({
        url: `v1/operation/add_service_in_multiple_campaigns`,
        method: "POST",
        body: data,
      }),
    }),
    getPostDetailsBasedOnFilter: builder.mutation({
      query: (data) => ({
        url: "v1/operation/get_post_details_based_on_filter",
        method: "POST",
        body: data,
      }),
    }),
    checkShortCodeUniqueness: builder.mutation({
      query: (shortCodes) => ({
        url: "v1/operation/check_shortcode_uniqueness",
        method: "POST",
        body: { shortCodes },
      }),
    }),
  }),
});

// Export hooks for each endpoint
export const {
  useGetAllPlanXDataQuery,
  useLazyFetchAllStatsQuery,
  useGetPlanWiseDataQuery,
  useGetPlanByIdQuery,
  useGetAllPagessByPlatformQuery,
  usePlanDataUploadMutation,
  usePlanDataUploadPlatformWiseMutation,
  usePostDataUpdateMutation,
  useVendorDataQuery,
  useAuditedDataUploadMutation,
  useLazyGetAuditLogHistoryQuery,
  useUpdatePhaseDateMutation,
  useUpdateVendorMutation,
  useUpdateMultipleAuditStatusMutation,
  useUpdatePriceforPostMutation,
  useGetPostDetailsBasedOnFilterMutation,
  useAddStoryDataMutation,
  useAddMultipleServiceMutation,
  useGetDeleteStoryDataQuery,
  useBulkCampaignUpdateMutation,
  useGetPostDetailofPagenVendorMutation,
  useCheckShortCodeUniquenessMutation,
  useHandleFetchPriceMutation,
} = OperationApi;

export default OperationApi;
